import {useEffect, useState} from "react";
import {
    Alert,
    Box, BreadcrumbGroup,
    Button,
    CopyToClipboard, Flashbar,
    Grid, Modal,
    SpaceBetween, Spinner,
} from "@cloudscape-design/components";
import {useAuth} from "react-oidc-context";
import {downloadFile, Endpoint, handleResponse, sendRequest} from "../../utils/api";
import {ArchitectureUtils} from "../../utils/architecture";
import {CollectionsTable} from "../../reusable_components/filteredTable/collectionsTable";
import {createColumnDefinitions, FILTERING_PROPERTIES, TEXTS} from "./table_config";
import CodeView from "@cloudscape-design/code-view/code-view";


const NoFilesAlert = () => (
    <Alert
        header="Infrastructure as Code project not generaterd"
    >
        The IaC project for this architecture hasn’t been generated yet. To create it, open the action menu in the top-right corner and select “Generate IaC project”.
    </Alert>
)


const ProjectGenerationErrorFlashbar = ({message}) => (
    <Flashbar items={
        [
            {
                type: "error",
                header: 'Failed to generate IaC project',
                content: message,
                dismissible: false,
                id: "generating_iac_error"
            }
        ]
    } />
)


const GeneratingProjectFlashbar = () => (
    <Flashbar items={
        [
            {
                type: "info",
                loading: true,
                header: 'Generating IaC project',
                content: "Please wait while the IaC project defining the architecture is being generated. The content will refresh automatically once the process is complete.",
                dismissible: false,
                id: "generating_project"
            }
        ]
    } />
)


export const IaCProject = ({architecture}) => {
    // Files
    const [path, setPath] = useState('')
    const [files, setFiles] = useState([])
    const [loadingFiles, setLoadingFiles] = useState(false)

    // File preview
    const [filePreviewVisible, setFilePreviewVisible] = useState(false)
    const [loadingFile, setLoadingFile] = useState(false)
    const [fileContent, setFileContent] = useState('')
    const [fileName, setFileName] = useState('')

    // Project download
    const [isDownloadingProject, setIsDownloadingProject] = useState(false)
    const [isGeneratingProject, setIsGeneratingProject] = useState(ArchitectureUtils.isGeneratingIaCProject(architecture))

    const auth = useAuth()
    const column_definitions = createColumnDefinitions(onFolderExpanded, previewFile)

    useEffect(() => {
        browsePath()
    }, [path])

    useEffect(() => {
        setIsGeneratingProject(ArchitectureUtils.isGeneratingIaCProject(architecture))
    }, [architecture?.IaCState])

    function browsePath() {
        setLoadingFiles(true)

        sendRequest(Endpoint.browseArchitectureFiles(auth.user.id_token, architecture?.id, path))
            .then(handleResponse)
            .then((response) => setFiles(response.Items))
            .finally(() => setLoadingFiles(false))
    }

    function previewFile(file) {
        setFileName(file.Name)
        setFilePreviewVisible(true)
        setLoadingFile(true)

        sendRequest(Endpoint.getFileContents(auth.user.id_token, architecture?.id, file.Key))
            .then(handleResponse)
            .then((response) => setFileContent(response.contents))
            .finally(() => setLoadingFile(false))
            .catch(() => setFilePreviewVisible(false))
    }

    function downloadIaCProject() {
        setIsDownloadingProject(true)
        const filename = architecture?.title.replaceAll(' ', '_') + '.zip'

        downloadFile(auth.user.id_token, architecture?.id, filename)
            .catch((err) => console.error(err))
            .finally(() => setIsDownloadingProject(false))
    }

    function onFolderExpanded(event) {
        event.preventDefault()
        setPath(path + event.detail.href + '/')
    }

    function onBreadcrumbFollowed(event) {
        event.preventDefault()

        if (event.detail.href === '/') setPath('')
        else setPath(event.detail.href)
    }

    function buildBreadcrumbs() {
        const components = path.split('/')
        let accumPath = ''
        let items = [{text: "Project files", href: "/"}]

        for (let c of components) {
            if (c.length > 0) {
                items.push({text: c, href: accumPath + c + '/'})
                accumPath = accumPath + c + '/'
            }
        }

        return items
    }

    return (
        <SpaceBetween size={'m'}>
            {isGeneratingProject ? (
                <GeneratingProjectFlashbar />
            ) : architecture?.IaCError ? (
                <ProjectGenerationErrorFlashbar message={architecture?.IaCError} />
            ) : !ArchitectureUtils.hasGeneratedIaCProject(architecture) ? (
                <NoFilesAlert/>
            ) : null }

            <CollectionsTable
                header={
                    <SpaceBetween direction={'vertical'} size={'xxs'}>
                        <Grid
                            gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}
                        >
                            <BreadcrumbGroup
                                onFollow={onBreadcrumbFollowed}
                                items={buildBreadcrumbs()}
                                ariaLabel="Breadcrumbs"
                            />

                            <Box float={'right'}>
                                <SpaceBetween direction={'horizontal'} size={'s'}>
                                    <Button iconName={'refresh'} onClick={browsePath} loading={loadingFiles}></Button>
                                    <Button disabled={!ArchitectureUtils.hasGeneratedIaCProject(architecture)} iconName={'download'} onClick={downloadIaCProject} loading={isDownloadingProject}>{`Download${isDownloadingProject? "ing" : ""} IaC project`}</Button>
                                </SpaceBetween>
                            </Box>
                        </Grid>

                        <Box color="text-body-secondary">These files define the infrastructure as code for the architecture. Select a file to preview its contents.</Box>
                    </SpaceBetween>
                }
                data={files}
                loading={loadingFiles}
                texts={TEXTS}
                filteringProperties={FILTERING_PROPERTIES}
                columnDefinitions={column_definitions}
                sortingColumn={column_definitions[0]}
                variant={'container'}
                stickyHeader={true}
            />

            <Modal
                size={'large'}
                visible={filePreviewVisible}
                onDismiss={() => setFilePreviewVisible(false)}
                header={fileName}
            >
                {loadingFile?
                    <Box textAlign={'center'} margin={{bottom: 'm'}}>
                        <Spinner size={'big'}></Spinner>
                    </Box> :
                    <CodeView
                        lineNumbers={true}
                        content={fileContent}
                        actions={
                            <CopyToClipboard
                                copyButtonAriaLabel="Copy code"
                                copyErrorText="Code failed to copy"
                                copySuccessText="Code copied"
                                textToCopy={fileContent}
                            />
                        }
                    />
                }
            </Modal>
        </SpaceBetween>
    )
}